import React, {useMemo} from 'react';
import styled from 'styled-components';
import {tint} from 'theme/helper';
import {useFormikContext} from 'formik';
import {CollapsibleFieldset} from 'components/customer/Product/Specs/CollapsibleFieldset';
import {DrawerFieldsRow} from 'components/customer/Product/Specs/DrawerFieldsRow';
import {DrawerHardwareDefaults} from 'components/customer/Product/Specs/DrawerHardwareDefaults';
import {FIELDSETS} from 'components/customer/Product/FieldSet';
import {Fieldset} from 'components/customer/Product/entity/Fieldset';
import {Drawer} from 'components/customer/Product/entity/Drawer';
import {
    cabinetDefault,
    faceSpace,
    followsCabinet,
    toNumber,
} from 'components/customer/Product/Drawer/helpers';
import {useDrawerParams} from 'components/customer/Product/Drawer/helpers/useDrawerParams';
import {useDrawerDefaults} from 'components/customer/Product/Drawer/DrawerDefaultsContext';
import {useProductContext} from 'contexts';
import {isFieldEnabled} from 'shared/helpers';
import {getVisibility} from 'shared/components/Forms/Field';

interface DrawerHardwareProps {
    fieldsets: Fieldset[];
    hasPreview?: boolean;
    // the drawer fieldset, which carries the type and runner field names
    runnerFieldset?: Fieldset;
}

interface HardwareValues {
    drawers?: Drawer[];
    [key: string]: unknown;
}

const formatMm = (value: number) => String(Math.round(value * 10) / 10);

const FACE_TYPE = 'drawer_face_type';

// The face type is a field among the margins, so it is listed with them while
// they are folded, but only where it is a choice: a standard door fixes it.
const faceTypeSummary = (
    fieldset: Fieldset,
    values: HardwareValues,
    materialOptions: object
): string => {
    const field = fieldset.fields?.find((item) => item.name === FACE_TYPE);

    if (
        !field ||
        !getVisibility(
            field,
            values[String(FACE_TYPE)],
            values,
            null,
            materialOptions
        )
    ) {
        return null;
    }

    const enabled = field.options?.enabled;

    if (enabled === false) return null;

    if (typeof enabled === 'string') {
        try {
            if (!isFieldEnabled(field, values, materialOptions)) return null;
        } catch (error) {
            return null;
        }
    }

    const option = field.options?.selectOptions?.find(
        (item) => String(item.value) === String(values[String(FACE_TYPE)])
    );

    return option ? `${field.displayName}: ${option.label}` : null;
};

export const DrawerHardware = ({
    fieldsets,
    hasPreview = false,
    runnerFieldset,
}: DrawerHardwareProps) => {
    const {values} = useFormikContext<HardwareValues>();
    const {defaults} = useDrawerDefaults();
    const {getMaterialOptions} = useProductContext<{
        getMaterialOptions: () => object;
    }>();
    const drawerParams = useDrawerParams(
        runnerFieldset?.fields?.[0],
        runnerFieldset
    );

    // matches getFaceHeight(), only positive margins come off the shared space
    const spaceLeft = useMemo(() => {
        const total = toNumber(values.cabinet_total_drawer_height);

        if (total <= 0) return null;

        const top = toNumber(values.cabinet_drawer_top);
        const bottom = toNumber(values.cabinet_drawer_bottom);
        const gap = toNumber(values.cabinet_drawer_gap);
        const drawerCount = values.drawers?.length ?? 0;

        const space = faceSpace(total, top, bottom, gap, drawerCount);

        return space > 0 ? `${formatMm(space)}mm left for faces` : null;
    }, [values]);

    // how many drawers still carry the cabinet's hardware
    const scope = useMemo(() => {
        const drawers = values.drawers ?? [];
        const typeField = drawerParams.drawerTypeFieldName;
        const runnerField = drawerParams.drawerRunnerFieldName;

        if (drawers.length === 0 || !typeField || !runnerField) return null;

        const cabinet = {
            type: cabinetDefault(drawers, typeField, defaults.type),
            runner: cabinetDefault(drawers, runnerField, defaults.runner),
        };

        const following = drawers.filter((drawer) =>
            followsCabinet(drawer, cabinet, typeField, runnerField)
        ).length;
        const overridden = drawers.length - following;

        if (overridden === 0) {
            return `applies to all ${drawers.length} drawer${
                drawers.length === 1 ? '' : 's'
            }`;
        }

        return `${following} drawer${following === 1 ? ' uses' : 's use'} the default · ${overridden} overridden`;
    }, [values.drawers, drawerParams, defaults]);

    // a recessed handle sets the gap and top margin, so the margins open to
    // show them; None is -1
    const recessedHandle =
        toNumber(values.fingerPull_styles) > 0
            ? values.fingerPull_styles
            : null;

    const drawerFields = fieldsets.find(
        (fieldset) => fieldset.name === FIELDSETS.DRAWER_FIELDS
    );

    if (fieldsets.length === 0) return null;

    return (
        <Card data-cy="drawer-hardware-card">
            <CardHeader>
                Drawer Hardware
                {scope === null ? null : (
                    <SubHeading data-cy="drawer-hardware-scope">
                        {scope}
                    </SubHeading>
                )}
            </CardHeader>
            {fieldsets
                .filter((fieldset) => fieldset !== drawerFields)
                .map((fieldset) => (
                    <CollapsibleFieldset
                        key={fieldset.name}
                        fieldset={fieldset}
                        hasPreview={hasPreview}
                        aside={
                            fieldset.name === FIELDSETS.DRAWER_MARGINS
                                ? spaceLeft
                                : null
                        }
                        enabledPrefix={
                            fieldset.name === FIELDSETS.DOOR_EDGES
                                ? 'Edged'
                                : undefined
                        }
                        summaryExtra={
                            fieldset.name === FIELDSETS.DRAWER_MARGINS
                                ? faceTypeSummary(
                                      fieldset,
                                      values,
                                      getMaterialOptions()
                                  )
                                : undefined
                        }
                        expandOn={
                            fieldset.name === FIELDSETS.DRAWER_MARGINS
                                ? recessedHandle
                                : undefined
                        }
                    />
                ))}
            {drawerFields ? (
                <DrawerFieldsRow
                    fieldset={drawerFields}
                    hasPreview={hasPreview}
                />
            ) : null}
            {runnerFieldset?.fields?.length ? (
                <DrawerHardwareDefaults
                    field={runnerFieldset.fields[0]}
                    fieldset={runnerFieldset}
                />
            ) : null}
        </Card>
    );
};

// no overflow: hidden, it clips dropdowns opened inside the card
const Card = styled.div`
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(20, 60, 110, 0.14);
    margin-bottom: 14px;
`;

const SubHeading = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: #6b7480;
`;

const CardHeader = styled.div`
    border-radius: 6px 6px 0 0;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 18px;
    border-bottom: 1px solid
        ${({theme}) => tint(theme.colors.primary?.main, 0.12)};
    background: ${({theme}) => tint(theme.colors.primary?.main, 0.05)};
    font-size: 15px;
    font-weight: 700;
    color: ${({theme}) => theme.colors.primary.main};
`;
